import React, { createContext, useState, useContext, useEffect } from "react";
import { CircularProgress, Box } from "@mui/material";

// Create Auth Context
const AuthContext = createContext(null);

const PERMISSIONS = {
  is_staff: [
    "can_manage_clients",
    "can_manage_users",
    "can_scan",
    "can_view_records",
    "can_view_analytics",
    "can_manage_products",
  ],
  is_worker: ["can_scan", "can_view_records", "can_view_analytics"],
  is_customer: ["can_view_own_data"],
};

// Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize user from localStorage on component mount
  useEffect(() => {
    const initializeAuth = () => {
      try {
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          setUser(JSON.parse(storedUser));
        }
      } catch (error) {
        console.error("Error initializing auth:", error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Show loading spinner while initializing
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Login function - now uses backend API
  const login = async (username, password, role) => {
    try {
      const { authAPI } = await import("../services/AuthService");

      const result = await authAPI.login(username, password, role);

      if (result.success && result.user) {
        const userData = {
          id: result.user.id,
          username: result.user.username,
          email: result.user.email,
          role: result.user.role,
          permissions: result.user.permissions,
          is_staff: result.user.is_staff,
          is_superuser: result.user.is_superuser,
          client_id: result.user.client_id,
        };

        // Store user in state and localStorage
        setUser(userData);
        localStorage.setItem("user", JSON.stringify(userData));

        return true;
      }

      console.log("Login failed:", result.message);
      return false;
    } catch (error) {
      console.error("Login error:", error);
      return false;
    }
  };

  // Customer login function
  const customerLogin = async (email, password) => {
    try {
      // In a real application, you would make an API call here
      // For now, we'll check the clients in localStorage
      const savedClients = localStorage.getItem("clients");
      if (!savedClients) {
        return false;
      }

      const clients = JSON.parse(savedClients);

      // Find client by email (using email as the login identifier)
      // For demo, we'll use a standard password "client123" for all clients
      const foundClient = clients.find(
        (client) => client.email === email && password === "client123"
      );

      if (foundClient) {
        const clientData = {
          id: foundClient.id,
          username: foundClient.name,
          name: foundClient.name,
          companyName: foundClient.companyName,
          email: foundClient.email,
          role: "client",
          clientId: foundClient.id,
          isClient: true,
          permissions: ["is_client"], // Only client permissions, no scan or analytics
        };

        // Store client in state and localStorage
        setUser(clientData);
        localStorage.setItem("user", JSON.stringify(clientData));

        return true;
      }

      return false;
    } catch (error) {
      console.error("Customer login error:", error);
      return false;
    }
  };

  // Register function
  const register = async (userData) => {
    try {
      // In a real application, you would make an API call here
      // For demo purposes, just simulate successful registration
      console.log("Registering user:", userData);

      // Simulate successful registration
      return true;
    } catch (error) {
      console.error("Registration error:", error);
      return false;
    }
  };

  // Logout function
  const logout = () => {
    setUser(null);
    localStorage.removeItem("user");
  };

  // Update user profile
  const updateUserProfile = (updatedData) => {
    if (user) {
      const updatedUser = {
        ...user,
        ...updatedData,
      };

      // Update state and localStorage
      setUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));

      return true;
    }

    return false;
  };

  // Check if user is authenticated
  const isAuthenticated = () => {
    return !!user;
  };

  // Check if user has specific permission
  const hasPermission = (permission) => {
    if (!user || !user.permissions) return false;
    return Array.isArray(user.permissions)
      ? user.permissions.includes(permission)
      : false;
  };

  // Check if user is superuser
  const isSuperUser = () => {
    return hasPermission("is_superuser");
  };

  // Check if user is a client
  const isClient = () => {
    return hasPermission("is_client");
  };

  // Client login function
  const clientLogin = async (email, password) => {
    setLoading(true);

    try {
      // In a real app, this would be an API call
      const savedClients = localStorage.getItem("clients");
      if (!savedClients) {
        throw new Error("No clients found");
      }

      const clients = JSON.parse(savedClients);
      const client = clients.find((c) => c.email === email);

      if (!client || password !== (client.password || "client123")) {
        // Default password for testing
        throw new Error("Invalid email or password");
      }

      // Create client user with role
      const clientUser = {
        id: Date.now().toString(),
        name: client.name,
        email: client.email,
        role: "client",
        clientId: client.id,
        isClient: true,
        permissions: ["is_client"], // Only grant client-specific permissions
      };

      setUser(clientUser);
      localStorage.setItem("currentUser", JSON.stringify(clientUser));
      localStorage.setItem("isAuthenticated", "true");
      return clientUser;
    } catch (error) {
      console.error("Client login error:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Context value
  const value = {
    user,
    loading,
    login,
    customerLogin,
    register,
    logout,
    updateUserProfile,
    isAuthenticated,
    hasPermission,
    isSuperUser,
    isClient,
    clientLogin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
